/* eslint-disable arrow-body-style */
import _ from 'underscore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Promise } from 'bluebird';

import { CSMLAssetSource, ROOT_FOLDER_ID, useAdsStudioServices, fetchImgPromise } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { DataContext } from '@bingads-webui-campaign-react/ads-studio-shared-components';

import { useFilterAndBlur } from './use-filter-and-blur';
import { useAdPlacement } from './use-ad-placement';
import { useBackgroundChange } from './background-change/use-background-change';
import { ViewName, convertMetaData, revertMetaData } from './utils';

export const useEditImageState = ({
  imageId,
  dataService,
  editImageUrl,
  perfMarker,
  setDataLoaded,
  permissions,
  logger,
  i18n,
  getSmartCroppings,
  navigation,
  initialCurrentView,
  folderId,
  getAssetEnhancementSettings,
}) => {
  const [currentView, setCurrentView] = useState(ViewName.PlacementListView);
  const [originalImage, setOriginalImage] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [initialAssetLoading, setInitialAssetLoading] = useState(false);
  const [backgroundChangedResult, setBackgroundChangedResult] = useState(null);
  const [savedBackgroundColorChangedImage, setSavedBackgroundColorChangedImage] = useState([]);
  const [filterBlurBaseImage, setFilterBlurBaseImage] = useState(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [assetsAIEnhancementEnabled, setAssetsAIEnhancementEnabled] = useState(false);
  const serverContext = useContext(ServerContext);
  const { setData } = useContext(DataContext);
  const cropDataService = useAdsStudioServices();

  const { cropImage, imageCroppingTasks } = cropDataService;

  const isImageFittingEnabled = useMemo(() => _.result(permissions, ['dynamic', 'ImageFittingMVP'], false) && !_.result(permissions, ['dynamic', 'ImageFittingExlcudeMAE'], true)
    && _.result(permissions, ['dynamic', 'ImageFittingUnifiedImageROI'], false), [permissions]);
  const isBrandKitEnabled = useMemo(() => _.result(permissions, ['dynamic', 'BrandKitUI'], false), [permissions]);

  const finalImageFittingEnabled = useMemo(() => isImageFittingEnabled && assetsAIEnhancementEnabled, [isImageFittingEnabled, assetsAIEnhancementEnabled]);

  useEffect(() => {
    if (initialCurrentView) {
      setCurrentView(initialCurrentView);
    }
  }, [initialCurrentView]);

  useEffect(() => {
    // We need the image url, image width and height for the background removal
    if (editImageUrl) {
      setLoadingStatus(true);
      fetchImgPromise(editImageUrl).then((image) => {
        setOriginalImage({
          url: editImageUrl,
          width: image.naturalWidth,
          height: image.naturalHeight,
          originalWidth: image.naturalWidth,
          originalHeight: image.naturalHeight,
          sourceType: CSMLAssetSource.Shutterstock,
        });
        setLoadingStatus(false);
      });
    } else if (!originalImage.url) {
      setLoadingStatus(true);
      setInitialAssetLoading(true);
      perfMarker.willFetchData();
      getAssetEnhancementSettings().then((settings) => {
        const assetsAIEnhancementEnabledInner = settings.AssetAIEnhancementOptout === 'false';
        const promises = [
          dataService.getImageById({ imageId, isImageFittingEnabled: isImageFittingEnabled && assetsAIEnhancementEnabledInner }),
          isBrandKitEnabled ? isCopilotRegionRestricted({
            feature: 'displayTemplate',
            permissions,
            serverContext,
          }) : Promise.resolve(true),
        ];
        Promise.all(promises).then(([res, isRestricted]) => {
          if (res.baseId && res.metadata && (res.metadata.effect || res.metadata.combinedEffect)) {
            // applied smart effect, get to the base image
            const { filter, blur } = revertMetaData(res.metadata) || {};
            dataService.getImageById({ imageId: res.baseId, isImageFittingEnabled: isImageFittingEnabled && assetsAIEnhancementEnabledInner }).then((image) => {
              setFilterBlurBaseImage({ ...image, filter, blur });
              setOriginalImage(res);
              setLoadingStatus(false);
              setInitialAssetLoading(false);
            });
          } else if (isBrandKitEnabled && !isRestricted && res.metadata && res.metadata.template &&
            res.metadata.template.templateId &&
            Number(res.metadata.template.version) >= 1.2
          ) {
            // template based AIGC image, navigate to the edit display image page
            setData({
              imageId: res.id,
              url: res.url,
              metadata: res.metadata,
              height: res.height,
              width: res.width,
              sourceType: res.sourceType,
            });
            navigation.gotoEditDisplayImagePage({
              imageId,
              folderId,
              templateId: res.metadata.template.templateId,
            });
          } else {
            setOriginalImage(res);
            setLoadingStatus(false);
            setInitialAssetLoading(false);
          }
        });
        setAssetsAIEnhancementEnabled(assetsAIEnhancementEnabledInner);
      });
    }
  }, [dataService, editImageUrl, imageId, isImageFittingEnabled, originalImage.url, navigation, folderId, isBrandKitEnabled, permissions, serverContext]);

  const {
    filterIndex,
    setFilterIndex,
    updateImage,
    backgroundBlurEnabled,
    toggleBackgroundBlur,
    changeBackgroundBlurLevel,
    backgroundBlurLevel,
    resultImage: resultFilterAndBlurImage,
    imagesWithFilter,
    getImagesWithFilter,
    isImageUpdated: isFilterAndBlurImageUpdated,
    isImageChangedWhenActive: isFilterAndBlurImageChangedWhenActive,
  } = useFilterAndBlur({
    dataService,
    // eslint-disable-next-line no-nested-ternary
    originalImage: editImageUrl ? '' : (filterBlurBaseImage ? filterBlurBaseImage.url : originalImage.url), // if editImageUrl is set, it means the image is from shutter stock which is not supported by adjust
    setLoadingStatus,
    initialFilter: filterBlurBaseImage ? filterBlurBaseImage.filter : null,
    initialBlur: filterBlurBaseImage ? filterBlurBaseImage.blur : null,
    isActive: currentView === ViewName.ImageAdjustView,
  });

  const {
    getSegmentations,
    generateBackground,
    drawColorBackground,
    imageUrlWithColoredBackground,
    imageUrlWithoutBackground, // the image url without background
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    getMaskValueAtPoint,
    segmentationMasks,
    setSelectedImageSrc,
    selectedImageSrc, // the image after user selected objects
    selectedMasks,
    setSelectedMasks,
    fetchSegmentationError,
    isFetchingSegmentations,
    maskValues,
  } = useBackgroundChange({
    dataService,
    perfMarker,
    logger,
  });

  const adPlacementResult = useAdPlacement({
    image: originalImage,
    permissions,
    setDataLoaded,
    logger,
    i18n,
    getSmartCroppings,
    setLoadingStatus,
    imageCroppingTasks,
    isImageFittingEnabled: finalImageFittingEnabled,
    dataService,
  });

  const {
    updateImage: updateAdPlacementImageInner,
    cropSettingsToUpdate,
    hasCropSettingsChanged,
    hasCroppingTask,
    requiredSizeInvalidRatioList,
    allViewErrorTip,
  } = adPlacementResult;

  const {
    saveImageCopy, uploadImageToFolder, saveImageWithBaseId, getImageById, uploadStockImage, saveImage, moveImageToFolder, addImageWithBaseIdToFolder,
  } = dataService;

  const folderIdNumber = useMemo(() =>
    (folderId && folderId !== ROOT_FOLDER_ID ? Number(folderId) : null), [folderId]);

  const replaceBackground = useCallback((backgroundChangedImage, needToSave) => {
    setLoadingStatus(true);
    if (needToSave) {
      // save the image to asset library first, since the image data is base64 which is not supported by filter and blur API
      uploadImageToFolder({
        imageData: backgroundChangedImage.url,
        folderId: folderIdNumber,
      }).then((res) => {
        logger.logTrace('edit image services: replace with single color background for image id', { imageId: res });
        getImageById({ imageId: res, isImageFittingEnabled: finalImageFittingEnabled }).then((image) => {
          updateImage(image).then(() => {
            setLoadingStatus(false);
          });
          setSavedBackgroundColorChangedImage([...savedBackgroundColorChangedImage, image.url]);
          setBackgroundChangedResult(image);
          setCurrentView(ViewName.ImageAdjustView);
        });
      });
    } else {
      logger.logTrace('edit image services: replace with generated background', { imageData: backgroundChangedImage.url });
      setBackgroundChangedResult(backgroundChangedImage);
      setCurrentView(ViewName.ImageAdjustView);
      updateImage(backgroundChangedImage).then(() => {
        setLoadingStatus(false);
      });
    }
    setImageUrlWithGeneratedBackground(null);
  }, [setImageUrlWithGeneratedBackground, uploadImageToFolder, folderIdNumber, logger, getImageById, finalImageFittingEnabled, updateImage, savedBackgroundColorChangedImage]);

  const isUnsavedStockImage = useMemo(() => !_.isEmpty(editImageUrl), [editImageUrl]);

  const isSavedStockImage = useMemo(() => _.get(originalImage, 'sourceType') === CSMLAssetSource.Shutterstock, [originalImage]);

  const isStockImage = useMemo(() => isUnsavedStockImage || isSavedStockImage, [isSavedStockImage, isUnsavedStockImage]);

  const isImageUpdated = useMemo(() => (
    originalImage && resultFilterAndBlurImage && (isFilterAndBlurImageUpdated || backgroundChangedResult)
  ), [originalImage, resultFilterAndBlurImage, isFilterAndBlurImageUpdated, backgroundChangedResult]);

  const cropImageAndGoBack = useCallback((id, backToLibraryPage) => {
    const { placementSettings } = cropSettingsToUpdate;
    if (hasCropSettingsChanged) {
      cropImage({
        imageId: id,
        placementSettings,
      }).then(() => {
        setLoadingStatus(false);
        if (backToLibraryPage) {
          navigation.gotoLibraryPage();
        } else {
          navigation.goBack();
        }
      });
    } else {
      setLoadingStatus(false);
      if (backToLibraryPage) {
        navigation.gotoLibraryPage();
      } else {
        navigation.goBack();
      }
    }
  }, [cropImage, cropSettingsToUpdate, hasCropSettingsChanged, navigation]);

  const onConfirmDialogSave = useCallback(() => {
    if (isImageUpdated) {
      // When the url is changed, need to update asset. Then save its crop changes if have.
      setLoadingStatus(true);
      saveImage({
        id: originalImage.id,
        type: originalImage.type,
        url: resultFilterAndBlurImage.url,
      })
        .then(() => {
          cropImageAndGoBack(originalImage.id);
        });
    } else {
      setLoadingStatus(true);
      cropImageAndGoBack(originalImage.id);
    }
  }, [cropImageAndGoBack, isImageUpdated, originalImage, resultFilterAndBlurImage, saveImage]);

  const saveImageDirectly = useCallback(() => {
    if (isUnsavedStockImage) {
      // When it's an unsaved stock image, upload it first. Then save its crop changes if have.
      setLoadingStatus(true);
      uploadStockImage({ stockImageId: imageId })
        .then((id) => {
          cropImageAndGoBack(id, true);
        });
    } else if (isImageUpdated || hasCropSettingsChanged) {
      setIsConfirmDialogOpen(true);
    } else {
      navigation.goBack();
    }
  }, [cropImageAndGoBack, hasCropSettingsChanged, imageId, isImageUpdated, isUnsavedStockImage, navigation, uploadStockImage]);

  const updateAdPlacementImage = useCallback(() => {
    if ((isImageUpdated || isFilterAndBlurImageChangedWhenActive) && resultFilterAndBlurImage.url !== originalImage.url) {
      const image = new Image();
      image.src = resultFilterAndBlurImage.url;
      image.onload = () => {
        updateAdPlacementImageInner({
          url: resultFilterAndBlurImage.url,
          width: image.width,
          height: image.height,
          originalWidth: image.width,
          originalHeight: image.height,
        });
      };
    } else if ((isImageUpdated || isFilterAndBlurImageChangedWhenActive) && resultFilterAndBlurImage.url === originalImage.url) {
      updateAdPlacementImageInner(originalImage);
    }
  }, [isImageUpdated, isFilterAndBlurImageChangedWhenActive, resultFilterAndBlurImage.url, originalImage, updateAdPlacementImageInner]);

  const saveBackgroundChangedResult = useCallback(() => {
    return new Promise((resolve) => {
      // image already saved in asset library, return directly
      if (backgroundChangedResult && savedBackgroundColorChangedImage.includes(backgroundChangedResult.url)) {
        logger.logTrace('edit image services: save single color background');
        resolve(backgroundChangedResult);
      // save background changed image to asset library
      } else if (backgroundChangedResult) {
        saveImageCopy({
          image: backgroundChangedResult,
          folderId: folderIdNumber,
        }).then((res) => {
          logger.logTrace('edit image services: save generated background for image id', { imageId: res, imageUrl: backgroundChangedResult.url });
          resolve({ id: res, url: backgroundChangedResult.url });
        });
      // no background changed image, return original image id
      } else {
        const baseImage = filterBlurBaseImage || originalImage;
        resolve(baseImage);
      }
    });
  }, [backgroundChangedResult, filterBlurBaseImage, folderIdNumber, logger, originalImage, saveImageCopy, savedBackgroundColorChangedImage]);

  const moveImageToFolderInner = useCallback((id) => {
    if (folderId && folderId !== ROOT_FOLDER_ID) {
      return getImageById({ imageId: id }).then((image) => {
        return moveImageToFolder({
          image,
          destFolderId: folderIdNumber,
        });
      });
    }
    return Promise.resolve();
  }, [folderId, folderIdNumber, getImageById, moveImageToFolder]);

  const saveAsCopy = useCallback(() => {
    if (isImageUpdated) {
      setLoadingStatus(true);
      // save background changed image to asset library first
      saveBackgroundChangedResult().then((res) => {
        // save the image with filter and blur
        if (resultFilterAndBlurImage && resultFilterAndBlurImage.url !== res.url) {
          const metaData = convertMetaData(resultFilterAndBlurImage);
          if (finalImageFittingEnabled) {
            addImageWithBaseIdToFolder({
              imageSrc: resultFilterAndBlurImage.url,
              baseId: Number(res.id),
              folderId: folderIdNumber,
              metaData: JSON.stringify(metaData),
            }).then((resInner) => {
              logger.logTrace('edit image services: save image as copy with baseId', { id: resInner, baseId: res.id });
              cropImageAndGoBack(resInner);
            });
          } else {
            saveImageWithBaseId({
              image: {
                url: resultFilterAndBlurImage.url,
                metaData,
              },
              baseId: res.id,
            }).then((inner) => {
              // currently MT doesn't support to save image with baseId to a folder, so move it to the folder
              logger.logTrace('edit image services: save image as copy with baseId', { id: inner, baseId: res.id });
              moveImageToFolderInner(inner).then(() => {
                // crop the image if needed
                cropImageAndGoBack(inner);
              });
            });
          }
        } else {
          const { id } = res || {};
          logger.logTrace('edit image services: save image as copy', { id });
          cropImageAndGoBack(res.id);
        }
      });
    } else {
      navigation.goBack();
    }
  }, [addImageWithBaseIdToFolder, cropImageAndGoBack, finalImageFittingEnabled, folderIdNumber, isImageUpdated, logger, moveImageToFolderInner, navigation, resultFilterAndBlurImage, saveBackgroundChangedResult, saveImageWithBaseId]);

  const disableSave = useMemo(() => (
    (!isUnsavedStockImage && !isImageUpdated && !hasCropSettingsChanged) || hasCroppingTask || (hasCropSettingsChanged && requiredSizeInvalidRatioList && requiredSizeInvalidRatioList.length > 0)
  ), [isUnsavedStockImage, isImageUpdated, hasCropSettingsChanged, hasCroppingTask, requiredSizeInvalidRatioList]);

  const saveButtonTooltip = useMemo(() => {
    let tooltip;
    if (disableSave && hasCropSettingsChanged && requiredSizeInvalidRatioList && requiredSizeInvalidRatioList.length > 0) {
      tooltip = allViewErrorTip;
    }

    return tooltip;
  }, [allViewErrorTip, disableSave, hasCropSettingsChanged, requiredSizeInvalidRatioList]);

  return {
    originalImage,
    resultImage: resultFilterAndBlurImage,
    currentView,
    setCurrentView,
    filterIndex,
    setFilterIndex,
    updateImage,
    backgroundBlurEnabled,
    toggleBackgroundBlur,
    changeBackgroundBlurLevel,
    backgroundBlurLevel,
    loadingStatus,
    initialAssetLoading,
    saveAsCopy,
    saveImageDirectly,
    imagesWithFilter,
    getImagesWithFilter,
    isImageUpdated,
    replaceBackground,
    isStockImage,
    disableSave,
    haveCroppingTasks: !_.isEmpty(imageCroppingTasks),
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    onConfirmDialogSave,
    saveButtonTooltip,
    updateAdPlacementImage,
    ...adPlacementResult,

    // Change Background
    generateBackground,
    getSegmentations,
    drawColorBackground,
    imageUrlWithColoredBackground,
    imageUrlWithoutBackground,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    getMaskValueAtPoint,
    segmentationMasks,
    setSelectedImageSrc,
    selectedImageSrc, // the image after user selected objects
    selectedMasks,
    setSelectedMasks,
    maskValues,
    fetchSegmentationError,
    isFetchingSegmentations,
  };
};
